import { add, getChargesAmountRemaining } from '@willow/shared-iso';

import { PortalSelectedLoan } from '../../App';

export const getTotalAmountDue = (loan: PortalSelectedLoan, ignoreWorkoutPlans: boolean = false): number => {
  const {
    currentSnapshot: {
      totalAmountRemainingFromBorrower,
      totalWorkoutPlanAmountRemainingFromBorrower,
      activeWorkoutPlan,
      maturityConfig: { isMaturing },
      expectedPayoffTotal,
      isFullInterestOnlyLoan,
      interestDueAtPayoff,
      interestAccruedToDate,
      charges,
    },
  } = loan;

  if (interestDueAtPayoff) {
    return add(interestAccruedToDate, getChargesAmountRemaining(charges));
  }

  if (isMaturing && !isFullInterestOnlyLoan && totalAmountRemainingFromBorrower === 0) {
    return expectedPayoffTotal;
  }

  if (
    !ignoreWorkoutPlans &&
    activeWorkoutPlan &&
    ['ForbearanceWorkoutPlan', 'FlexModificationWorkoutPlan', 'RepaymentWorkoutPlan'].includes(
      activeWorkoutPlan.__typename ?? '',
    )
  ) {
    return totalWorkoutPlanAmountRemainingFromBorrower ?? 0;
  }

  return totalAmountRemainingFromBorrower;
};

export const getIsForbearanceOrFlexModificationWorkoutPlan = (
  activeWorkoutPlan: PortalSelectedLoan['currentSnapshot']['activeWorkoutPlan'],
): boolean =>
  Boolean(
    activeWorkoutPlan &&
    ['ForbearanceWorkoutPlan', 'FlexModificationWorkoutPlan'].includes(activeWorkoutPlan.__typename ?? ''),
  );

export interface FullPaymentDueInput {
  totalAmountDue: number;
  totalUpcomingAmountDue: number;
  totalStatementAmountDue: number;
  hasActiveWorkoutPlan: boolean;
  isForbearanceOrFlexModificationWorkoutPlan: boolean;
}

export interface FullPaymentDue {
  amount: number;
  includesUpcomingStatements: boolean;
}

export const getFullPaymentDue = ({
  totalAmountDue,
  totalUpcomingAmountDue,
  totalStatementAmountDue,
  hasActiveWorkoutPlan,
  isForbearanceOrFlexModificationWorkoutPlan,
}: FullPaymentDueInput): FullPaymentDue => {
  const onlyChargesCurrentlyDue =
    totalAmountDue > 0 &&
    totalStatementAmountDue === 0 &&
    !hasActiveWorkoutPlan &&
    totalUpcomingAmountDue > totalAmountDue;

  if (totalAmountDue > 0 && !onlyChargesCurrentlyDue) {
    return { amount: totalAmountDue, includesUpcomingStatements: false };
  }

  return isForbearanceOrFlexModificationWorkoutPlan
    ? { amount: 0, includesUpcomingStatements: false }
    : { amount: totalUpcomingAmountDue, includesUpcomingStatements: true };
};
